import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { 
  Menu, 
  X, 
  User, 
  LogOut, 
  LayoutDashboard, 
  Car, 
  Calendar,
  Plus,
  Search,
  Home,
  ChevronRight,
  Shield,
  MessageCircle,
  Heart,
  Wallet
} from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useTranslation();

  const isActive = (path) => location.pathname === path;

  // Cerrar menú cuando cambia la ruta
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Bloquear scroll cuando el menú está abierto
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate("/");
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { path: "/explore", label: t('nav.explore'), icon: Search },
    { path: "/plazafy-gate", label: "Plazafy Gate", icon: Shield, isNew: true },
  ];

  const userLinks = [
    { path: "/dashboard", label: t('nav.dashboard'), icon: LayoutDashboard },
    { path: "/my-spots", label: t('nav.mySpots'), icon: Car },
    { path: "/my-bookings", label: t('nav.myBookings'), icon: Calendar },
    { path: "/host/payments", label: "Cobros", icon: Wallet },
    { path: "/favorites", label: "Favoritos", icon: Heart },
    { path: "/messages", label: t('nav.messages') || "Mensajes", icon: MessageCircle },
    { path: "/profile", label: t('nav.profile'), icon: User },
  ];

  // Link de admin (solo visible para admins)
  const adminLink = { path: "/admin", label: t('nav.admin'), icon: Shield };

  // Obtener URL del avatar
  const getAvatarUrl = () => {
    if (!user?.avatar) return null;
    if (user.avatar.startsWith('http')) return user.avatar;
    return `${window.location.origin}${user.avatar}`;
  };

  const avatarUrl = getAvatarUrl();

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <>
      <nav className="glass fixed top-0 left-0 right-0 z-50 -my-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_garagespot/artifacts/9v94qfyu_ik2eevmn_E97AFB72-AE7D-42EF-9C28-41681E1BB841.png" 
                alt="Plazafy" 
                style={{ height: '120px' }}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link flex items-center gap-1 ${isActive(link.path) ? "active" : ""}`}
                  data-testid={`nav-${link.path.slice(1)}`}
                >
                  {link.label}
                  {link.isNew && (
                    <span className="px-1.5 py-0.5 bg-violet-500 text-white text-[10px] rounded-full font-medium">
                      Nuevo
                    </span>
                  )}
                </Link>
              ))}

              {user ? (
                <>
                  {/* Solo mostrar "Publicar Plaza" si NO es admin */}
                  {user.role !== "admin" && (
                    <Link to="/add-spot">
                      <Button 
                        className="bg-violet-500 hover:bg-violet-600 text-white glow-violet-hover"
                        data-testid="add-spot-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {t('nav.addSpot')}
                      </Button>
                    </Link>
                  )}

                  {/* Si es admin, mostrar botón directo a Administración */}
                  {user.role === "admin" && (
                    <Link to="/admin">
                      <Button 
                        className="bg-violet-500 hover:bg-violet-600 text-white glow-violet-hover"
                        data-testid="admin-panel-btn"
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        {t('nav.admin')}
                      </Button>
                    </Link>
                  )}

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        className="flex items-center gap-2 text-gray-300 hover:text-white hover:bg-white/5"
                        data-testid="user-menu-trigger"
                      >
                        <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center overflow-hidden">
                          {avatarUrl ? (
                            <img 
                              src={avatarUrl} 
                              alt={user?.full_name || user?.name || user?.username || "Usuario"} 
                              className="w-full h-full object-cover"
                              data-testid="navbar-avatar-img"
                            />
                          ) : (
                            <User className="w-4 h-4 text-violet-400" />
                          )}
                        </div>
                        <span className="hidden lg:inline">{user?.full_name || user?.name || user?.username || "Usuario"}</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56 bg-[#231f30] border-white/10">
                      {/* Para admin: Administración, Mi Perfil y Cerrar Sesión */}
                      {user.role === "admin" ? (
                        <>
                          <DropdownMenuItem
                            onClick={() => navigate(adminLink.path)}
                            className="text-violet-400 hover:text-violet-300 hover:bg-violet-500/10 cursor-pointer"
                            data-testid="menu-admin"
                          >
                            <Shield className="w-4 h-4 mr-2" />
                            {adminLink.label}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => navigate("/profile")}
                            className="text-gray-300 hover:text-white hover:bg-white/5 cursor-pointer"
                            data-testid="menu-profile"
                          >
                            <User className="w-4 h-4 mr-2" />
                            {t('nav.profile')}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-white/10" />
                          <DropdownMenuItem
                            onClick={handleLogout}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer"
                            data-testid="logout-btn"
                          >
                            <LogOut className="w-4 h-4 mr-2" />
                            {t('nav.logout')}
                          </DropdownMenuItem>
                        </>
                      ) : (
                        <>
                          {userLinks.map((link) => (
                            <DropdownMenuItem
                              key={link.path}
                              onClick={() => navigate(link.path)}
                              className="text-gray-300 hover:text-white hover:bg-white/5 cursor-pointer"
                              data-testid={`menu-${link.path.slice(1)}`}
                            >
                              <link.icon className="w-4 h-4 mr-2" />
                              {link.label}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator className="bg-white/10" />
                          <DropdownMenuItem
                            onClick={handleLogout}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer"
                            data-testid="logout-btn"
                          >
                            <LogOut className="w-4 h-4 mr-2" />
                            {t('nav.logout')}
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <div className="flex items-center gap-3">
                  <Link to="/login">
                    <Button 
                      variant="ghost" 
                      className="text-gray-300 hover:text-white hover:bg-white/5"
                      data-testid="login-btn"
                    >
                      {t('nav.login')}
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button 
                      className="bg-violet-500 hover:bg-violet-600 text-white glow-violet-hover"
                      data-testid="register-btn"
                    >
                      {t('nav.register')}
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden text-gray-300 hover:text-white p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <>
          {/* Overlay */}
          <div 
            className="mobile-menu-overlay md:hidden"
            onClick={closeMobileMenu}
          />
          
          {/* Drawer */}
          <div className="mobile-menu-drawer md:hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <span className="text-white font-semibold text-lg">{t('nav.home')}</span>
              <button 
                onClick={closeMobileMenu}
                className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/5"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* User info (if logged in) */}
            {user && (
              <div className="p-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {avatarUrl ? (
                      <img 
                        src={avatarUrl} 
                        alt={user?.full_name || user?.name || user?.username || "Usuario"} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-lg font-bold text-violet-400">
                        {(user?.full_name || user?.name || user?.username || "U").charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-medium truncate">{user?.full_name || user?.name || user?.username || "Usuario"}</p>
                    <p className="text-gray-400 text-sm truncate">@{user.username}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Links */}
            <div className="flex-1 py-2">
              <Link
                to="/"
                onClick={closeMobileMenu}
                className={`mobile-menu-item ${isActive('/') ? 'active' : ''}`}
              >
                <Home className="w-5 h-5" />
                <span className="flex-1">{t('nav.home')}</span>
                <ChevronRight className="w-4 h-4 opacity-50" />
              </Link>

              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={closeMobileMenu}
                  className={`mobile-menu-item ${isActive(link.path) ? 'active' : ''}`}
                >
                  <link.icon className="w-5 h-5" />
                  <span className="flex-1 flex items-center gap-2">
                    {link.label}
                    {link.isNew && (
                      <span className="px-1.5 py-0.5 bg-violet-500 text-white text-[10px] rounded-full font-medium">
                        Nuevo
                      </span>
                    )}
                  </span>
                  <ChevronRight className="w-4 h-4 opacity-50" />
                </Link>
              ))}

              {user ? (
                <>
                  {/* Divider */}
                  <div className="my-2 mx-4 border-t border-white/10" />
                  
                  {/* Para admin: Administración, Mensajes y Mi Perfil */}
                  {user.role === "admin" ? (
                    <>
                      <Link
                        to="/admin"
                        onClick={closeMobileMenu}
                        className={`mobile-menu-item ${isActive('/admin') ? 'active' : ''}`}
                        style={{ background: 'rgba(139, 92, 246, 0.1)' }}
                      >
                        <Shield className="w-5 h-5 text-violet-400" />
                        <span className="flex-1 text-violet-400">{t('nav.admin')}</span>
                        <ChevronRight className="w-4 h-4 opacity-50" />
                      </Link>
                      <Link
                        to="/messages"
                        onClick={closeMobileMenu}
                        className={`mobile-menu-item ${isActive('/messages') ? 'active' : ''}`}
                      >
                        <MessageCircle className="w-5 h-5" />
                        <span className="flex-1">{t('nav.messages') || "Mensajes"}</span>
                        <ChevronRight className="w-4 h-4 opacity-50" />
                      </Link>
                      <Link
                        to="/profile"
                        onClick={closeMobileMenu}
                        className={`mobile-menu-item ${isActive('/profile') ? 'active' : ''}`}
                      >
                        <User className="w-5 h-5" />
                        <span className="flex-1">{t('nav.profile')}</span>
                        <ChevronRight className="w-4 h-4 opacity-50" />
                      </Link>
                    </>
                  ) : (
                    <>
                      {/* Para usuarios normales: todas las opciones */}
                      {userLinks.map((link) => (
                        <Link
                          key={link.path}
                          to={link.path}
                          onClick={closeMobileMenu}
                          className={`mobile-menu-item ${isActive(link.path) ? 'active' : ''}`}
                        >
                          <link.icon className="w-5 h-5" />
                          <span className="flex-1">{link.label}</span>
                          <ChevronRight className="w-4 h-4 opacity-50" />
                        </Link>
                      ))}

                      {/* Publicar Plaza Button - solo para usuarios normales */}
                      <Link
                        to="/add-spot"
                        onClick={closeMobileMenu}
                        className="mobile-menu-item mobile-menu-item-primary"
                      >
                        <Plus className="w-5 h-5" />
                        <span>{t('nav.addSpot')}</span>
                      </Link>
                    </>
                  )}

                  {/* Divider */}
                  <div className="my-2 mx-4 border-t border-white/10" />

                  {/* Logout */}
                  <button
                    onClick={handleLogout}
                    className="mobile-menu-item mobile-menu-item-danger w-full"
                  >
                    <LogOut className="w-5 h-5" />
                    <span className="flex-1 text-left">{t('nav.logout')}</span>
                  </button>
                </>
              ) : (
                <>
                  {/* Divider */}
                  <div className="my-2 mx-4 border-t border-white/10" />
                  
                  <Link
                    to="/login"
                    onClick={closeMobileMenu}
                    className="mobile-menu-item"
                  >
                    <User className="w-5 h-5" />
                    <span className="flex-1">{t('nav.login')}</span>
                    <ChevronRight className="w-4 h-4 opacity-50" />
                  </Link>

                  <Link
                    to="/register"
                    onClick={closeMobileMenu}
                    className="mobile-menu-item mobile-menu-item-primary"
                  >
                    <Plus className="w-5 h-5" />
                    <span>{t('auth.createAccount')}</span>
                  </Link>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
